import { InputCheckbox } from '../jdscheckbox/generated';
import { InputCode } from '../jdsinputcode/generated';
import { InputDate } from '../jdsinputdate/generated';
import { InputDropdown } from '../jdsdropdown/generated';
import { InputField } from '../jdsinputfield/generated';
import { InputRadio } from '../jdsradiobutton/generated';
import { InputSlider } from '../jdsslider/generated';
import { InputTextarea } from '../jdstextarea/generated';
import { InputToggle } from '../jdstoggle/generated';
import { MenuBlockItemProps } from '../jdsmenublockitem/logic';
import { forwardRef } from 'react';

type ValueTypes = string | boolean | number;
// type SupportedHTMLInputs = HTMLInputElement;

export type ExcludeInheritedProps = 'prefix' | 'size' | 'value';

// prefix is duplicated on DOM's native attributes
type InputGroupProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement> &
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  ExcludeInheritedProps
>;

export const InputType = {
  text: 'text',
  textarea: 'textarea',
  number: 'number',
  email: 'email',
  password: 'password',
  checkbox: 'checkbox',
  toggle: 'toggle',
  radio: 'radio',
  code: 'code',
  dropdown: 'dropdown',
  range: 'range',
  date: 'date'
} as const;

export interface StateConfig {
  /**
   * The error thrown when the input is not in the correct format.
   * e.g. an email input with invalid email value, or a text input not
   * matching its `pattern`.
   */
  invalidError?: string;
  /**
   * The error thrown when a required input has been left empty
   */
  requiredError?: string;
  /**
   * Sets error text on input
   */
  errorText?: string;
  /**
   * Sets success text on input
   */
  successText?: string;
  /**
   * Sets warning text on input
   */
  warningText?: string;
}

export interface InputPropsGeneric extends InputGroupProps {
  /**
   * Type of input. These correspond to the full list of input types  as can be seen at [w3schools html_form_input_types](https://www.w3schools.com/html/html_form_input_types.asp). Extra types like textarea, dropdown are also supported through the this element.
   * You can pass `type=custom` with children to bind generic input behaviours on your child item.
   */
  type: string | keyof typeof InputType;
  /**
   * The name attribute of the input
   */
  name: string;
  /**
   * Determines the label
   */
  label: string;
  /**
   * Determines the placeholder
   */
  placeholder?: string;
  /**
   * Shows description below the input. You can also pass an element here in case
   * you want to show links in helperText.
   */
  helperText?: string | JSX.Element;
  /**
   * Optinally sets the `value` attribute of html inputs. Children of `TextArea` can also be passed
   * using this prop. Available types of value?: `string | boolean | number`.
   */
  value?: ValueTypes;
  /**
   * Determines the state of the Input. This can be dynamically changed as per your form handling.
   * Provide accompanying text via the `stateConfig` prop.
   */
  state?: 'error' | 'warning' | 'success';
  /**
   * The group of various state text
   * Show only one state text on the below the input
   *
   * `invalidError` - The error thrown when the input is not in the correct format<br>
   * `requiredError` - The error thrown when a required input has been left empty<br>
   * `errorText` - Set error text on input<br>
   * `successText` - Set success text on input<br>
   * `warningText` - Set warning text on input<br>
   *
   */
  stateConfig?: StateConfig;
  /**
   * If this input is required.
   */
  required?: boolean;
  /**
   * Select this to enable `checkbox`, `toggle` and `radio` by default. To dynamically
   * check them, use `checked` as prop
   */
  defaultChecked?: boolean;
  /**
   * Adjusts the size of the input for `checkbox`, `radio` and `toggle`.
   */
  size?: 'small' | 'medium';
  /**
   * An element to be placed on the left, ideally an icon. This is clickable if onPrefixClick is defined.
   */
  prefix?: JSX.Element;
  /**
   * An element to be placed on the right, ideally an icon. This is clickable if onSuffixClick is defined.
   */
  suffix?: JSX.Element;
  /**
   * Click event for prefix element. Passing this makes the prefix clickable.
   * @param value current value of the input
   */
  onPrefixClick?: (value?: ValueTypes) => void;
  /**
   * Click event for suffix element. Passing this makes the suffix clickable. Please note this event will not work if Input type is dropdown.
   * @param value current value of the input
   */
  onSuffixClick?: (value?: ValueTypes) => void;
  /**
   * Called when the input value is invalid
   * @param event Default HTML FormEvent on onInvalid
   */
  onInvalid?: (event: React.FormEvent<any>) => void;
  /**
   * Programmatically set validity of input on change. Return true if input should become invalid. Use `StateConfig.invalidError` to determine what error message to show when input becomes invalid.
   * @param value current value of the input. Can be string, numbers or boolean (for checkboxes, toggles).
   * @return true if the input is invalid
   */
  setValidity?: (value?: ValueTypes) => boolean;
  /**
   * Use this to obtain ref object of `<input>` element.
   */
  forwardRef?: React.RefObject<any>;
  /**
   * Use this for custom input fields like non native dropdown.We can pass `<select>` as a children to non native dropdown. All props relevant to inputs will
   * be binded automatically to the root element of children.
   */
  children?: any;
}

export interface InputProps extends InputPropsGeneric {
  /**
   * When used with text based input, determines the input format.
   * e.g. XY## #### to allow `XY12 345` values.
   * <br/>
   * `#`: `0-9`
   * <br/>
   * `&`: `A-Z, a-z`
   * <br/>
   * `*`: `A-Z, a-z, 0-9`
   * <br/>
   * `pattern` prop is also set internally to assist with invalid error states. You can overwrite this by passing explicit `pattern' prop.
   * This will not work on `checkbox`, `radio`, `toggle`, `number`, `code` and `textarea`.
   */
  textMask?: string | Array<string | RegExp>;
  /**
   * Used to provide type specific props for in-depth customisation.
   */
  typeConfig?: {
    toggle?: {
      labelPosition?: 'left' | 'right';
    };
    code?: {
      /**
       * Determines the total boxes for numeric code
       */
      length?: number;
    };
    checkbox?: {
      /**
       * Determines the indeterminate state for checkbox
       */
      indeterminate?: boolean;
    };
    range?: {
      editableTextBox?: boolean;
    };
    dropdown?: {
      /** ¯
       * Use this for non native dropdown. All the items will be displayed in the dropdown menu
       */
      items?: MenuBlockItemProps[];
      /**
       * When set to true, data from items will be shown as native/device dropdown menu.
       * @deprecated The native dropdown will be removed the the future
       */
      nativeMenu?: boolean;
    };
  };
  ariaLabel?: string;
  ariaDescribedby?: string;
  ariaInvalid?: string;
}

export type InputStateType = {
  state?: 'error' | 'success' | 'warning';
  errorText?: string;
  value?: ValueTypes;
  propState?: 'error' | 'success' | 'warning';
};

/**
 * @deprecated Please replace this component with indiviual components depending on the `type`.
 * Ex. use `InputField` for `type=text`. Please refer https://ds.jio.com/react/storybook for all the components. This
 * component will be removed in the upcoming versions. Also, no bug fixes will be done on this component
 * @param props
 * @returns
 */
const LegacyInput = forwardRef<HTMLInputElement, InputProps>(
  (props: InputProps, ref) => {
    switch (props.type) {
      case 'textarea':
        return <InputTextarea {...props} />;
      case 'checkbox':
        return <InputCheckbox {...props} {...props.typeConfig?.checkbox} />;
      case 'toggle':
        return <InputToggle {...props} {...props.typeConfig?.toggle} />;
      case 'radio':
        return <InputRadio {...props} />;
      case 'code':
        return (
          <InputCode codeLength={props.typeConfig?.code?.length} {...props} />
        );
      case 'dropdown':
        return (
          <InputDropdown {...props} items={props.typeConfig?.dropdown?.items} />
        );
      case 'range':
        return (
          <InputSlider
            {...props}
            editableTextBox={props.typeConfig?.range?.editableTextBox}
          />
        );
      case 'date':
        return <InputDate {...props} />;
      default:
        return <InputField {...props} ref={ref} />;
    }
  }
);

export default LegacyInput;
